import { useCallback, useEffect, useRef, useState } from 'react'
import { postSubmit } from '@ui-seeker/utils'

import { BACKEND_APIS } from '@constants'
import { createLatestGate, shouldPaint } from '../../../../@components/KindPillsField/latestOnly'


const DEBOUNCE_MS = 400

/**
 * The data of a BI dashboard (CU-869fmhmx8 D12; shared since CU-869fmjzrh S2): asks 400 ms after the last change, keeps the previous table
 * under "Updating…", cancels the previous request and paints only the latest one. Cancelling does not save
 * BigQuery (the server finishes and caches it); the wait does. One attempt, no retry: a 400 or a 5xx is shown
 * as it is instead of being sent twice.
 *
 * `body` null = not ready (no office yet).
 */
export default function useDashboardData(body) {
	// `dataKey` / `errorKey`: the view each outcome answers (CU-869fmjzrg QA), so a screen can tell an old answer from its own
	const [state, setState] = useState({ data: null, dataKey: null, loading: false, error: null, errorKey: null })
	const gate = useRef(createLatestGate())
	const controller = useRef(null)
	const key = body ? JSON.stringify(body) : null
	const [attempt, setAttempt] = useState(0)

	useEffect(() => {
		if (!key) return undefined
		// a new view: whatever is still in flight answers the previous one and must not paint (nor end "Updating…")
		gate.current.next()
		const timer = setTimeout(() => {
			controller.current?.abort()
			const ctrl = new AbortController()
			controller.current = ctrl
			const id = gate.current.next()
			setState(s => ({ ...s, loading: true, error: null }))

			postSubmit(`${BACKEND_APIS.DASHBOARD}/results`, JSON.parse(key), { signal: ctrl.signal, retries: 1 })
				.then(data => {
					if (shouldPaint(gate.current, id)) setState({ data, dataKey: key, loading: false, error: null, errorKey: null })
				})
				.catch(error => {
					if (shouldPaint(gate.current, id, error)) setState(s => ({ ...s, loading: false, error: error || new Error('request failed'), errorKey: key }))
				})
		}, DEBOUNCE_MS)
		return () => clearTimeout(timer)
	}, [key, attempt])

	useEffect(() => () => controller.current?.abort(), [])

	const retry = useCallback(() => setAttempt(n => n + 1), [])

	return { ...state, retry }
}
